/**
 * Coveristic Category Folders & Image Storage Utilities
 *
 * Strict product image Categories folders:
 * canvas, general, hats, hoodies, keychains, mugs, phone-cases, photo-frames, stickers, tshirts, wood-crafts
 */

export const VALID_IMAGE_CATEGORY_FOLDERS = [
  'canvas',
  'general',
  'hats',
  'hoodies',
  'keychains',
  'mugs',
  'phone-cases',
  'photo-frames',
  'stickers',
  'stories',
  'tshirts',
  'wood-crafts'
] as const;

export type ImageCategoryFolder = typeof VALID_IMAGE_CATEGORY_FOLDERS[number];

/**
 * Normalizes any category string into one of the 11 strictly approved category image folders
 */
export function normalizeCategoryFolder(input?: string): ImageCategoryFolder {
  if (!input) return 'general';
  
  // Clean string: lowercase, remove special characters, collapse hyphens
  let clean = input
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9_\-\s]/g, '')
    .replace(/[\s_]+/g, '-');

  // Specific alias mappings
  if (['t-shirts', 't-shirt', 'tshirts', 'tshirt', 'shirts', 'shirt', 'tees', 'apparel', 'custom-apparel'].includes(clean)) {
    return 'tshirts';
  }
  if (['phone-cases', 'phone-case', 'phonecases', 'cases', 'case', 'phone', 'phones'].includes(clean)) {
    return 'phone-cases';
  }
  if (['photo-frames', 'photo-frame', 'photoframes', 'frames', 'frame', 'pictures'].includes(clean)) {
    return 'photo-frames';
  }
  if (['wood-crafts', 'wood-craft', 'woodcrafts', 'woodcraft', 'wood', 'wooden', 'wooden-crafts'].includes(clean)) {
    return 'wood-crafts';
  }
  if (['canvas', 'canvas-art', 'canvases', 'wall-art', 'art-canvas'].includes(clean)) {
    return 'canvas';
  }
  if (['stickers', 'sticker', 'decals', 'decal', 'labels'].includes(clean)) {
    return 'stickers';
  }
  if (['mugs', 'mug', 'cups', 'cup', 'drinking-mugs', 'drinkware'].includes(clean)) {
    return 'mugs';
  }
  if (['hats', 'hat', 'caps', 'cap', 'snapbacks', 'headwear'].includes(clean)) {
    return 'hats';
  }
  if (['hoodies', 'hoodie', 'sweatshirts', 'sweatshirt'].includes(clean)) {
    return 'hoodies';
  }
  if (['keychains', 'keychain', 'keys', 'key-rings', 'key-ring'].includes(clean)) {
    return 'keychains';
  }
  if (['stories', 'story', 'lookbook', 'drops', 'coveristic-stories'].includes(clean)) {
    return 'stories';
  }

  // Exact match with approved list
  if ((VALID_IMAGE_CATEGORY_FOLDERS as readonly string[]).includes(clean)) {
    return clean as ImageCategoryFolder;
  }

  return 'general';
}

/**
 * Ensures an image path adheres to /images/{category}/{filename}
 */
export function sanitizeProductImagePath(imagePath: string, category: string, fallbackName = 'product'): string {
  if (!imagePath || typeof imagePath !== 'string') {
    return '';
  }

  const catFolder = normalizeCategoryFolder(category);

  // If it's already an absolute or relative URL to /images/{catFolder}/...
  if (imagePath.startsWith(`/images/${catFolder}/`)) {
    return imagePath;
  }

  // If it references /images/{someOtherCat}/..., replace with the correct category folder
  const imagesMatch = imagePath.match(/^\/images\/[^/]+\/(.+)$/);
  if (imagesMatch && imagesMatch[1]) {
    return `/images/${catFolder}/${imagesMatch[1]}`;
  }

  return imagePath;
}
